/**
 * Mg Theme — Recently viewed products (localStorage stack)
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Two components:
 *   MgRecentlyViewedManager  — utility object (CRUD + cap + dedupe)
 *   MgRecentlyViewed         — <mg-recently-viewed> custom element
 *                                 (renders cards via Section Rendering API)
 *
 * Tracker is inline in snippets/mg-recently-viewed-tracker.liquid
 * (rendered once on PDP, pushes current handle to the stack).
 */

(function () {
  if (window.MgRecentlyViewedManager) return;

  var KEY = 'mg-recently-viewed';
  var CAP = 12;

  var MgRecentlyViewedManager = {
    KEY: KEY,
    CAP: CAP,

    getItems: function () {
      try {
        var raw = window.localStorage.getItem(KEY);
        var items = raw ? JSON.parse(raw) : [];
        return Array.isArray(items) ? items : [];
      } catch (e) {
        return [];
      }
    },

    setItems: function (items) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify(items));
      } catch (e) {
        /* private mode / quota — silent */
      }
    },

    push: function (entry) {
      if (!entry || !entry.handle) return;
      var items = this.getItems();
      items = items.filter(function (i) { return i.handle !== entry.handle; });
      items.unshift({ handle: entry.handle, id: entry.id || null, viewed_at: Date.now() });
      if (items.length > CAP) items.length = CAP;
      this.setItems(items);
    },

    getHandles: function (excludeHandle) {
      return this.getItems()
        .map(function (i) { return i.handle; })
        .filter(function (h) { return h && h !== excludeHandle; });
    }
  };

  window.MgRecentlyViewedManager = MgRecentlyViewedManager;
})();

if (!customElements.get('mg-recently-viewed')) {
  class MgRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.maxItems = parseInt(this.getAttribute('data-max-items'), 10) || 8;
      this.excludeHandle = this.getAttribute('data-exclude-handle') || '';
      this.fetchSection = this.getAttribute('data-fetch-section') || 'mg-recently-viewed-fetch';
      this.grid = this.querySelector('[data-recently-viewed-grid]');
      this._render();
    }

    disconnectedCallback() {
      this._initialized = false;
      if (this._renderCtrl) this._renderCtrl.abort();
    }

    _render() {
      var manager = window.MgRecentlyViewedManager;
      if (!manager) {
        this._hide();
        return;
      }
      var handles = manager.getHandles(this.excludeHandle).slice(0, this.maxItems);
      if (handles.length === 0 || !this.grid) {
        this._hide();
        return;
      }
      var self = this;
      if (this._renderCtrl) this._renderCtrl.abort();
      this._renderCtrl = new AbortController();
      var ctrl = this._renderCtrl;

      /* Reserve layout space immediately with skeletons (anti-CLS) — reveal the
         section NOW, before the async fetch, so cards swap in place instead of
         the whole section appearing late and shifting content below it. */
      this.grid.innerHTML = handles.map(function () {
        return '<li><div class="mg-recently-viewed__skeleton" aria-hidden="true">'
          + '<div class="mg-skeleton mg-skeleton--card"></div>'
          + '<div class="mg-skeleton mg-skeleton--line"></div>'
          + '<div class="mg-skeleton mg-skeleton--text"></div></div></li>';
      }).join('');
      this.removeAttribute('hidden');

      var fetcher = (window.Mg && window.Mg.section && window.Mg.section.fetch) || this._fallbackFetch;
      /* Markets-safe: prefix the active locale sub-folder (routes.root). */
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var promises = handles.map(function (handle) {
        return fetcher.call(window.Mg ? window.Mg.section : null, root + 'products/' + encodeURIComponent(handle), self.fetchSection, ctrl.signal)
          .then(function (html) { return self._extractCard(html); })
          .catch(function () { return null; });
      });
      Promise.all(promises).then(function (cards) {
        if (self._renderCtrl !== ctrl) return; // superseded by a newer render
        var rendered = cards.filter(Boolean);
        if (rendered.length === 0) {
          self.grid.innerHTML = '';
          self._hide();
          return;
        }
        self.grid.innerHTML = rendered.map(function (markup) { return '<li>' + markup + '</li>'; }).join('');
        self.removeAttribute('hidden');
      });
    }

    _extractCard(html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var card = doc.querySelector('.mg-product-card');
        return card ? card.outerHTML : null;
      } catch (e) {
        return null;
      }
    }

    _fallbackFetch(url, sectionId, signal) {
      return fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId), signal ? { signal: signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error('fetch failed');
          return response.text();
        });
    }

    _hide() {
      this.setAttribute('hidden', '');
    }
  }

  customElements.define('mg-recently-viewed', MgRecentlyViewed);
}
